// Shared review-queue state for Order Intake (OI-3). Used by the Ops-reviewer
// variant in salesIntake and the Mfg-accepter ManufacturingOrderIntakePanel.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  acceptOrderIntake,
  cancelOrderIntake,
  holdOrderIntake,
  listOrderIntakes,
  returnOrderIntakeToHold,
  type OpsOrderIntakeRow,
  type OpsOrderIntakeStatus,
} from "@/lib/orderIntakeClient";

export type IntakeTab = OpsOrderIntakeStatus;

export const INTAKE_PAGE_SIZES = [10, 15, 25] as const;
export type IntakePageSize = (typeof INTAKE_PAGE_SIZES)[number];
export const DEFAULT_INTAKE_PAGE_SIZE: IntakePageSize = 10;

export const INTAKE_TABS: ReadonlyArray<{ id: IntakeTab; label: string }> = [
  { id: "pending_review", label: "Pending Review" },
  { id: "on_hold", label: "On Hold" },
  { id: "accepted", label: "Accepted" },
  { id: "cancelled", label: "Cancelled" },
];

export type UseOrderIntakeReviewState = {
  activeTab: IntakeTab;
  setActiveTab: (tab: IntakeTab) => void;
  goToTab: (tab: IntakeTab) => Promise<void>;
  rows: OpsOrderIntakeRow[];
  total: number | null;
  loadingRows: boolean;
  listError: string | null;
  selectedId: string | null;
  selectedRow: OpsOrderIntakeRow | null;
  setSelectedId: (id: string | null) => void;
  actionBusy: boolean;
  actionError: string | null;
  acceptSelected: (plantOverride?: string | null) => Promise<boolean>;
  holdSelected: (reason: string) => Promise<boolean>;
  cancelSelected: () => Promise<boolean>;
  returnSelectedToHold: (reason: string) => Promise<boolean>;
  refresh: () => Promise<void>;
  pageIndex: number;
  setPageIndex: (n: number) => void;
  pageSize: IntakePageSize;
  setPageSize: (n: IntakePageSize) => void;
  pageCount: number;
  firstShown: number;
  lastShown: number;
  pageSizeOptions: readonly IntakePageSize[];
};

export function useOrderIntakeReview(opts?: {
  paginate?: boolean;
}): UseOrderIntakeReviewState {
  const paginate = opts?.paginate === true;
  const [activeTab, setActiveTabState] = useState<IntakeTab>("pending_review");
  const [pageIndex, setPageIndexState] = useState(0);
  const [pageSize, setPageSizeState] = useState<IntakePageSize>(DEFAULT_INTAKE_PAGE_SIZE);
  const [rows, setRows] = useState<OpsOrderIntakeRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingRows, setLoadingRows] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedId, setSelectedIdState] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const ticketRef = useRef(0);

  const loadTab = useCallback(async (tab: IntakeTab, pageOverride?: number) => {
    const ticket = ++ticketRef.current;
    const page = pageOverride ?? pageIndex;
    setLoadingRows(true);
    setListError(null);
    const r = await listOrderIntakes({
      status: tab,
      ...(paginate ? { limit: pageSize, offset: page * pageSize } : {}),
    });
    if (ticketRef.current !== ticket) return;
    setLoadingRows(false);
    if (!r.ok) {
      setRows([]);
      setTotal(null);
      setListError(r.error);
      return;
    }
    setRows(r.data.rows);
    setTotal(r.data.total);
  }, [pageIndex, pageSize, paginate]);

  useEffect(() => {
    void loadTab(activeTab);
  }, [activeTab, loadTab]);

  const pageCount = Math.max(1, Math.ceil((total ?? 0) / pageSize));
  const firstShown = !total ? 0 : pageIndex * pageSize + 1;
  const lastShown = !total ? 0 : Math.min(total, (pageIndex + 1) * pageSize);

  useEffect(() => {
    if (!paginate) return;
    if (pageIndex >= pageCount) setPageIndexState(0);
  }, [paginate, pageIndex, pageCount]);

  const setActiveTab = useCallback((tab: IntakeTab) => {
    setActiveTabState(tab);
    setPageIndexState(0);
    setSelectedIdState(null);
    setActionError(null);
  }, []);

  const setPageIndex = useCallback((n: number) => {
    setPageIndexState(n);
    setSelectedIdState(null);
  }, []);

  const setPageSize = useCallback((n: IntakePageSize) => {
    setPageSizeState(n);
    setPageIndexState(0);
    setSelectedIdState(null);
  }, []);

  const goToTab = useCallback(
    async (tab: IntakeTab) => {
      setActiveTabState(tab);
      setPageIndexState(0);
      setSelectedIdState(null);
      setActionError(null);
      await loadTab(tab, 0);
    },
    [loadTab],
  );

  const setSelectedId = useCallback((id: string | null) => {
    setSelectedIdState(id);
    setActionError(null);
  }, []);

  const selectedRow = selectedId ? rows.find((r) => r.id === selectedId) ?? null : null;

  const refresh = useCallback(async () => {
    await loadTab(activeTab);
  }, [activeTab, loadTab]);

  const runSelectedAction = useCallback(
    async (fn: () => Promise<{ ok: true } | { ok: false; error: string }>): Promise<boolean> => {
      if (!selectedId) return false;
      const doneId = selectedId;
      setActionBusy(true);
      setActionError(null);
      try {
        const r = await fn();
        if (!r.ok) {
          setActionError(r.error);
          return false;
        }
        setSelectedIdState(null);
        setRows((prev) => prev.filter((row) => row.id !== doneId));
        await loadTab(activeTab);
        return true;
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Request failed.");
        return false;
      } finally {
        setActionBusy(false);
      }
    },
    [activeTab, loadTab, selectedId],
  );

  const acceptSelected = useCallback(
    async (plantOverride?: string | null): Promise<boolean> => {
      if (!selectedId) return false;
      return runSelectedAction(() =>
        acceptOrderIntake({
          intake_id: selectedId,
          plant_id_override: plantOverride ?? undefined,
        }),
      );
    },
    [runSelectedAction, selectedId],
  );

  const holdSelected = useCallback(
    async (reason: string): Promise<boolean> => {
      const trimmed = reason.trim();
      if (!trimmed) {
        setActionError("Hold reason is required.");
        return false;
      }
      return runSelectedAction(() =>
        holdOrderIntake({ intake_id: selectedId ?? "", hold_reason: trimmed }),
      );
    },
    [runSelectedAction, selectedId],
  );

  const cancelSelected = useCallback(async (): Promise<boolean> => {
    if (!selectedId) return false;
    return runSelectedAction(() => cancelOrderIntake({ intake_id: selectedId }));
  }, [runSelectedAction, selectedId]);

  const returnSelectedToHold = useCallback(
    async (reason: string): Promise<boolean> => {
      const trimmed = reason.trim();
      if (!trimmed) {
        setActionError("Hold reason is required for Return-to-Hold.");
        return false;
      }
      return runSelectedAction(() =>
        returnOrderIntakeToHold({
          intake_id: selectedId ?? "",
          hold_reason: trimmed,
        }),
      );
    },
    [runSelectedAction, selectedId],
  );

  return {
    activeTab,
    setActiveTab,
    goToTab,
    rows,
    total,
    loadingRows,
    listError,
    selectedId,
    selectedRow,
    setSelectedId,
    actionBusy,
    actionError,
    acceptSelected,
    holdSelected,
    cancelSelected,
    returnSelectedToHold,
    refresh,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    pageCount,
    firstShown,
    lastShown,
    pageSizeOptions: INTAKE_PAGE_SIZES,
  };
}
